/**
 * AtlasAdminEditorModule — admin world-building editor.
 *
 * Phase 1: creative fly camera (Minecraft-style).
 *   Toggle with F once the editor is engaged (palette/outliner opened) or to
 *   exit; enter otherwise with `/fly` in the debug console or the debug menu
 *   (plain F is the wilds strike). While flying: WASD moves
 *   along the look direction, Space/E ascend, Ctrl/Q descend, Shift boosts, and
 *   the mouse looks (reusing the existing pointer-lock). The server avatar PARKS:
 *   AtlasAvatarModule skips its `move` send + local-transform apply while
 *   `atlas.flyMode` is true, so nothing reaches the server and other players see
 *   the avatar stand still.
 *
 * Phase 2: prefab palette + place/select/delete + persistence.
 *   B opens the palette — browse/search the 60 structure:/prop: prefabs,
 *   click one to set the brush. While in fly mode + pointer-locked, left-click:
 *   — if a placement mesh is hit, select it (green BoxHelper highlight);
 *   — else if a brush is set, ray-march the terrain and call adminPlaceObject.
 *   Delete/Backspace deletes the selected placement via adminDeleteObject.
 *   Placements arrive via adminPlacement table subscription and render through
 *   AssetModule (same prefab→renderAsset→GLB chain as structures/decorations).
 *
 * Phase 3: transform controls (numeric panel + TransformControls gizmo).
 *   Selecting a placement enters cursor-edit mode: pointer lock is released,
 *   the OS cursor becomes free, a nine-field numeric panel appears (pos/rot/scale)
 *   and a TransformControls gizmo attaches to the object. Dragging a gizmo handle
 *   or committing a numeric field both call adminTransformObject. Keys 1/2/3
 *   switch gizmo mode; X toggles grid/rotation/scale snapping; Escape deselects
 *   and returns to fly mode.  The numeric panel is the reliable transform path;
 *   the gizmo is the tactile overlay.
 *
 * Rendering (subscription + placements) runs for EVERY client — placements are
 * world content, rendered INSTANCED in play mode (wave 3) and as individual
 * meshes only during an editor session (fly/selection) so the raycast/gizmo path
 * operates unchanged. The editor UI/input itself is dev-gated (localhost /
 * atlas:debug); the server reducers are admin-cap-gated — use /grant-admin in the
 * debug console (backtick) to bootstrap.
 */
import * as THREE from 'three/webgpu';
import { TransformControls } from 'three/addons/controls/TransformControls.js';
import type { App, IModule } from 'enginex';
import { atlas } from '../state.js';
import { seatingOffset } from '../lib/structure_pivot.js';
import { bus } from '../bus.js';
import { makeSystem, Phase } from '../engine-compat.js';
import { meshHeight } from '../lib/terrain_stamp.js';
import { StaticInstancer, collectMeshParts } from '../lib/instancing.js';
import { registerLamps, type LampAnchor } from './StreetLampsModule.js';
import { STREETLIGHT_KEYS } from '../lib/biome_settlements.js';
import type { AssetModule } from './AssetModule.js';
import { activePreset } from '../lib/quality_tiers.js';
import { placementWanted, placementLodFor, PLACEMENT_CULL_EVERY } from '../lib/placement_cull.js';
import { placementColliderPlan } from '../lib/structure_seat.js';
import type { Box3Like } from '../lib/structure_pivot.js';
import { colliderRingWant } from '../lib/settle_hotspots.js';
import { ASSET_STORE_BASE } from '../config.js';
import type { ColliderProxy } from '../lib/collider_proxy.js';
import type { PhysicsModule } from './PhysicsModule.js';

/** Placed prefabs that EMIT LIGHT at night (feeds the StreetLampsModule registry). */
const LAMP_KEYS = new Set<string>([...STREETLIGHT_KEYS, 'decor:lamppost', 'prop:lamppost']);

// ── Placement colliders (2026-09-23) ─────────────────────────────────────────
// "Placements carry no colliders" — the Commons' ~1,500 admin placements
// (535 fountain centrepieces, 304 kiosk clusters, 18 comms towers, 4 whole
// civb_city6 structures …) were walk-through. A SOLID placement (a structure
// key, or a bbox ≥ 2.5 m × 2.5 m × 2 m tall — never a bench or a lamp;
// `placementSolidEligible`) gets a Rapier static that FOLLOWS ITS INSTANCE:
// added when the row is instanced inside the walking-collision ring the
// settlements use (`colliderRingWant`: 320 m in, 400 m out — the ring bounds
// the count; the tier's `farCull` bounds the instances), dropped with it; the
// cooked proxy when the catalog has one (fetched once per prefab key, never
// awaited by the loading gate), else the seated, scaled bbox.
/** Hard cap on live placement statics — a plaza of kiosks inside the ring is a few dozen. */
const PLACEMENT_COLLIDER_MAX = 512;
/** Whole-district civ keys: a city-block trimesh is real BVH memory — the settlements'
 *  rule reduces it to a 96-point convex hull, and so does this. */
const PLACEMENT_HULL_KEY_RE = /_(scifi|city)\d/;
/** A prefab with no `asset_collider` row yet (the subscription may still be landing) is re-asked after this. */
const PLACEMENT_PROXY_RETRY_MS = 10_000;

function hullOf(positions: number[]): ColliderProxy {
  const n = (positions.length / 3) | 0;
  const step = Math.max(1, Math.ceil(n / 96));
  const pts: number[] = [];
  for (let i = 0; i < n; i += step) pts.push(positions[i * 3], positions[i * 3 + 1], positions[i * 3 + 2]);
  return { shape: 'convex_hull', points: pts };
}

const FLY_SPEED = 28; // m/s
const FLY_BOOST = 96; // m/s while Shift held

// ─── Phase 2 binding shim ────────────────────────────────────────────────────────
//
// The admin_placement table + reducers are added by Phase 2's server publish, so
// the generated module_bindings don't know about them yet. These interfaces let
// the client typecheck (and work at runtime via the shim cast) before `spacetime
// generate` runs. The callback arities match the real SpacetimeDB 2.x SDK:
//   onInsert  → (ctx, row)
//   onUpdate  → (ctx, oldRow, newRow)
//   onDelete  → (ctx, row)
// (Verified against conn.db.account.onInsert / conn.db.playerState.onUpdate in
//  AtlasNetworkModule.ts — same `(_ctx, row)` / `(_ctx, _old, row)` shape.)

interface AdminPlacementRow {
  placementId: bigint;
  worldId: bigint;
  prefabKey: string;
  kind: string;
  posX: number;
  posY: number;
  posZ: number;
  rotX: number;
  rotY: number;
  rotZ: number;
  rotW: number;
  scaleX: number;
  scaleY: number;
  scaleZ: number;
  /** The placer (the real row carries it) — only MY inserts may settle my pending undo entries. */
  placedBy?: { isEqual(other: unknown): boolean };
}

interface AdminTable {
  iter(): Iterable<AdminPlacementRow>;
  onInsert(cb: (ctx: unknown, row: AdminPlacementRow) => void): void;
  onUpdate(cb: (ctx: unknown, oldRow: AdminPlacementRow, row: AdminPlacementRow) => void): void;
  onDelete(cb: (ctx: unknown, row: AdminPlacementRow) => void): void;
}

/** Phase 4: settlementName table shim (added in Phase 4 server publish). */
interface SettlementNameRow {
  siteId: bigint;
  name: string;
}

interface SettlementNameTable {
  iter(): Iterable<SettlementNameRow>;
}

interface AdminReducers {
  adminPlaceObject(a: {
    worldId: bigint;
    prefabKey: string;
    kind: string;
    posX: number;
    posY: number;
    posZ: number;
    rotX: number;
    rotY: number;
    rotZ: number;
    rotW: number;
    scaleX: number;
    scaleY: number;
    scaleZ: number;
  }): Promise<void>;
  adminDeleteObject(a: { placementId: bigint }): Promise<void>;
  /**
   * Phase 3: move/rotate/scale a placed object.
   * The reducer is already published (server Phase 2); this shim lets the
   * client call it before `spacetime generate` knows about it.
   */
  adminTransformObject(a: {
    placementId: bigint;
    posX: number; posY: number; posZ: number;
    rotX: number; rotY: number; rotZ: number; rotW: number;
    scaleX: number; scaleY: number; scaleZ: number;
  }): Promise<void>;
  debugGrantCommonsAdmin(a: Record<string, never>): Promise<void>;
  /** Phase 4: persist an admin-set display name for a Commons settlement site. */
  setSettlementName(a: { siteId: bigint; name: string }): Promise<void>;
}

// ─── Phase 4 types ────────────────────────────────────────────────────────────────

/** Object-transform snapshot used as the `before` value in undo entries. */
interface TransformSnapshot {
  posX: number; posY: number; posZ: number;
  rotX: number; rotY: number; rotZ: number; rotW: number;
  scaleX: number; scaleY: number; scaleZ: number;
}

/**
 * Undo entry. Two variants:
 *   `place`     — undo by calling adminDeleteObject.
 *   `transform` — undo by calling adminTransformObject with the `before` snapshot.
 *
 * NOTE: delete-undo and redo are intentionally deferred. Re-placing a deleted
 * object would produce a new server-generated placementId that breaks any
 * preceding undo entries referencing the old id.
 */
type UndoEntry =
  | { kind: 'place'; placementId: bigint }
  | { kind: 'transform'; placementId: bigint; before: TransformSnapshot };

// ─── Module ───────────────────────────────────────────────────────────────────────

function devGated(): boolean {
  const host = (typeof location !== 'undefined' ? location.hostname : '') || '';
  const local = host === 'localhost' || host === '127.0.0.1' || host === '[::1]' || host === '';
  let optIn = false;
  try { optIn = typeof localStorage !== 'undefined' && localStorage.getItem('atlas:debug') === '1'; }
  catch { /* storage blocked */ }
  return local || optIn;
}

export class AtlasAdminEditorModule implements IModule {
  readonly name = 'AtlasAdminEditor';

  // ── Phase 1: fly camera ──────────────────────────────────────────────────────
  private keys = new Set<string>();
  private canvas: HTMLCanvasElement | null = null;
  private prevFly = false;
  private hint: HTMLDivElement | null = null;
  private readonly fwd = new THREE.Vector3();
  private readonly right = new THREE.Vector3();
  private readonly up = new THREE.Vector3(0, 1, 0);
  private readonly move = new THREE.Vector3();

  // ── Phase 2: placement + selection ───────────────────────────────────────────
  private scene: THREE.Scene | null = null;
  private readonly placementGroup = new THREE.Group();
  /** placementId.toString() → Three.js instance (INDIVIDUAL representation — editor mode only) */
  private readonly placements = new Map<string, THREE.Object3D>();
  private subscribed = false;
  /** Boot loading screen: admin placements cached for the CURRENT world vs
   *  rendered (either representation — instanced handles or individual meshes). */
  placementsReadiness(): { subscribed: boolean; cachedForWorld: number; rendered: number } {
    let cached = 0;
    for (const row of this.rowCache.values()) if (row.worldId === atlas.currentWorldId) cached++;
    // A culled row is RESOLVED (the gate must not wait for what the tier never draws).
    return { subscribed: this.subscribed, cachedForWorld: cached, rendered: this.instHandles.size + this.placements.size + this.culled.size + [...this.renderHidden].filter((k) => !this.placements.has(k)).length };
  }

  // ── Wave-3: dual representation ───────────────────────────────────────────────
  // Play mode renders placements INSTANCED (one draw per geometry/material — the
  // r182 per-object-UBO win); entering the editor (fly/selection) swaps to the
  // individual-mesh representation above so the existing raycast/gizmo/outliner
  // code operates unchanged, and swaps back on exit. Non-editor clients (the
  // module's rendering now runs for EVERYONE, not just dev origins) stay instanced.
  /** placementId key → cached authoritative row — the source both representations build from. */
  private readonly rowCache = new Map<string, AdminPlacementRow>();
  /** Unified Edit Mode hooks: the individual-rep mesh of a placement (null while instanced). */
  placementMesh(placementId: string): THREE.Object3D | null { return this.placements.get(placementId) ?? null; }
  /** The pick root for the unified editor (individual representation only). */
  get placementRoot(): THREE.Group { return this.placementGroup; }
  /** The cached authoritative row. */
  placementRow(placementId: string): AdminPlacementRow | undefined { return this.rowCache.get(placementId); }
  /** Interior shells: hide/restore a placement's render AND its collider (a solid
   *  placement's static lives with its instanced representation, 2026-09-23). */
  setPlacementHidden(placementId: string, hidden: boolean): void {
    const m = this.placements.get(placementId);
    if (m) m.visible = !hidden;
    // AN INSTANCED PLACEMENT HIDES THROUGH THE INSTANCER (interiors programme, slice C, 2026-10-01 — slice A's item 5):
    // in play mode a placement has no mesh, so `visible = false` hid nothing and its render stood inside the shell.
    // Hidden: its instance handles are removed (as a settlement building's and a deed's are) and `renderHidden` keeps
    // `addInstanced`, the cull tick and a mode switch from putting them back; restored: it is instanced again.
    if (hidden) {
      this.renderHidden.add(placementId);
      this.colliderHidden.add(placementId); this.dropCollider(placementId);
      if (this.instHandles.has(placementId)) this.removeInstanced(placementId);
    } else {
      this.renderHidden.delete(placementId);
      this.colliderHidden.delete(placementId);
      const row = this.rowCache.get(placementId);
      if (row && this.renderMode === 'instanced' && !this.instHandles.has(placementId) && !this.instPending.has(placementId)) void this.addInstanced(row, false);
      else if (row && this.instHandles.has(placementId)) this.syncCollider(placementId, row);
    }
  }
  /** Placements an interior shell hid: not instanced, not shown (individual), no collider — until restored. */
  private readonly renderHidden = new Set<string>();
  /** The eight WORLD corners of a placement's rendered box, from its ROW and the bbox this client last instanced for
   *  its prefab (the seat the instanced render uses: row × footprint offset × bbox) — what the interior marker needs
   *  when the placement has no mesh (play mode). null until a placement of that prefab was instanced here. */
  placementCorners(placementId: string): { corners: Array<[number, number, number]>; yaw: number } | null {
    const row = this.rowCache.get(placementId);
    const bb = row ? this.bboxByKey.get(row.prefabKey) : undefined;
    if (!row || !bb) return null;
    const rowM = new THREE.Matrix4().compose(
      new THREE.Vector3(row.posX, row.posY, row.posZ),
      new THREE.Quaternion(row.rotX, row.rotY, row.rotZ, row.rotW),
      new THREE.Vector3(row.scaleX, row.scaleY, row.scaleZ),
    );
    const seat = seatingOffset(row.prefabKey, bb);
    if (seat) rowM.multiply(new THREE.Matrix4().makeTranslation(seat.x, seat.y, seat.z));
    const corners: Array<[number, number, number]> = [];
    const v = new THREE.Vector3();
    for (let i = 0; i < 8; i++) {
      v.set(i & 1 ? bb.max.x : bb.min.x, i & 2 ? bb.max.y : bb.min.y, i & 4 ? bb.max.z : bb.min.z).applyMatrix4(rowM);
      corners.push([v.x, v.y, v.z]);
    }
    const yaw = new THREE.Euler().setFromQuaternion(new THREE.Quaternion(row.rotX, row.rotY, row.rotZ, row.rotW), 'YXZ').y;
    return { corners, yaw };
  }
  /** Probe surface: is this placement instanced / hidden right now. */
  placementState(placementId: string): { row: boolean; instanced: boolean; individual: boolean; hidden: boolean; collider: boolean } {
    return { row: this.rowCache.has(placementId), instanced: this.instHandles.has(placementId), individual: this.placements.has(placementId), hidden: this.renderHidden.has(placementId), collider: this.colliderOn.has(placementId) };
  }
  private instancer: StaticInstancer | null = null;
  // ── Placement colliders (2026-09-23) — see the module-level note ──
  private appRef: App | null = null;
  private physicsRef: PhysicsModule | null | undefined; // undefined = not looked up yet (a phase-1 module; resolved lazily)
  /** prefab key → natural-scale bbox of the representation this client last instanced for it. */
  private readonly bboxByKey = new Map<string, Box3Like>();
  /** prefab key → its render asset id (the catalog lookup is a scan — done once per key at instancing). */
  private readonly assetByKey = new Map<string, bigint>();
  /** prefab key → its cooked collider proxy (null = fetched but unusable / no proxy registered). */
  private readonly proxies = new Map<string, ColliderProxy | null>();
  private readonly proxyLoads = new Set<string>();
  private readonly proxyRetryAt = new Map<string, number>();
  /** placement key → the shape of its live Rapier static (`placement:<id>`). */
  private readonly colliderOn = new Map<string, 'proxy' | 'box'>();
  /** Placements an interior shell hid — no collider while hidden. */
  private readonly colliderHidden = new Set<string>();
  private colliderCapWarned = false;
  private readonly instHandles = new Map<string, number[]>();
  private readonly instReleases = new Map<string, () => void>(); // representation refs (instanced rep)
  private readonly instLod = new Map<string, number>(); // the placementLod each instanced row was built at
  private readonly instPending = new Set<string>(); // rows whose representation is loading
  /** Rows beyond the tier's object draw distance (play mode) — resolved, not rendered (lib/placement_cull.ts). */
  private readonly culled = new Set<string>();
  private cullFrame = 0;
  private renderMode: 'instanced' | 'individual' = 'instanced';
  private placeholderGeo: THREE.BoxGeometry | null = null;
  private placeholderMat: THREE.MeshStandardMaterial | null = null;
  private brushKey: string | null = null;
  private brushKind = 'structure';
  private selectedId: bigint | null = null;
  private selectionHelper: THREE.BoxHelper | null = null;
  private crosshair: HTMLDivElement | null = null;
  private palette: HTMLDivElement | null = null;
  private paletteVisible = false;
  private readonly raycaster = new THREE.Raycaster();
  private mouseHandler: ((e: MouseEvent) => void) | null = null;
  private keyHandler2: ((e: KeyboardEvent) => void) | null = null;

  // ── Phase 4: undo stack ───────────────────────────────────────────────────────
  private readonly undoStack: UndoEntry[] = [];
  /**
   * Transform snapshot of the selected object at the moment it was selected.
   * Updated after each commitTransform so successive commits each capture the
   * correct before-state for their undo entry.
   */
  private _beforeTransform: TransformSnapshot | null = null;
  /**
   * Count of adminPlaceObject calls that are awaiting their server-side onInsert
   * event for undo tracking. Incremented on every local place/duplicate/paste call;
   * decremented and an undo entry is pushed when the matching onInsert fires.
   */
  private _pendingUndoCount = 0;

  // ── Phase 4: copy/paste ───────────────────────────────────────────────────────
  private clipboard: {
    prefabKey: string;
    kind: string;
    quaternion: THREE.Quaternion;
    scale: THREE.Vector3;
  } | null = null;

  // ── Phase 4: outliner panel ───────────────────────────────────────────────────
  private outlinerPanel: HTMLDivElement | null = null;
  private outlinerList: HTMLDivElement | null = null;
  private outlinerVisible = false;

  // ── Phase 3: transform controls ──────────────────────────────────────────────
  /** The selected object that gizmo + panel are bound to. */
  private gizmoTarget: THREE.Object3D | null = null;
  /**
   * TransformControls instance (lazy-created on first select).
   * Cast note: TransformControls constructor expects Camera from "three"; both
   * three and three/webgpu re-export Camera from the same Three.Core module so
   * they are structurally identical — we use `as unknown as import('three').Camera`
   * at the construction site to satisfy the type checker.
   */
  private gizmo: TransformControls | null = null;
  /**
   * The Three.js helper object returned by gizmo.getHelper() that we add to the
   * scene. In r182 the Controls-based API separates the logic object (gizmo) from
   * the visual helper (what you add to the scene).
   */
  private gizmoHelper: THREE.Object3D | null = null;
  /** Whether position/rotation/scale snapping is enabled. */
  private snapEnabled = false;
  /** The DOM panel containing the 9 numeric transform fields + uniform scale. */
  private transformPanel: HTMLDivElement | null = null;
  private panelInputs: {
    posX: HTMLInputElement; posY: HTMLInputElement; posZ: HTMLInputElement;
    rotX: HTMLInputElement; rotY: HTMLInputElement; rotZ: HTMLInputElement;
    scX: HTMLInputElement; scY: HTMLInputElement; scZ: HTMLInputElement;
    scU: HTMLInputElement;
  } | null = null;

  constructor(private readonly assets: AssetModule) {}

  init(app: App): void {
    // RENDERING runs for EVERYONE — admin placements are world content. (Previously
    // the whole module, subscription included, was dev-gated: non-localhost players
    // never saw placed objects at all. Wave-3 fix.) Play mode = instanced.
    this.scene = app.world.scene;
    this.appRef = app;
    this.scene.add(this.placementGroup);
    this.instancer = new StaticInstancer(this.scene, 4096, 'admin-placements');
    this.instancer.setRainLayer(true); // admin placements shelter (render wave 1)
    bus.on('atlas:account-ready', () => { this.subscribe(); this.loadExisting(); });
    // Play-mode distance cull + LOD follow for EVERY client (2026-09-08): the
    // 1,501 Commons placements at full LOD0 were 99 % of a potato frame.
    app.world.addSystem(makeSystem('AtlasPlacementCull', Phase.RenderSub, 38, () => this.cullTick()));
    (globalThis as unknown as { __placements?: () => unknown }).__placements = () => {
      let nearest: { x: number; z: number; d: number; key: string } | null = null;
      for (const row of this.rowCache.values()) {
        if (row.worldId !== atlas.currentWorldId) continue;
        const d = Math.hypot(row.posX - atlas.localX, row.posZ - atlas.localZ);
        if (!nearest || d < nearest.d) nearest = { x: row.posX, z: row.posZ, d, key: row.prefabKey };
      }
      let proxyCols = 0;
      for (const s of this.colliderOn.values()) if (s === 'proxy') proxyCols++;
      return {
        rows: this.rowCache.size, rendered: this.instHandles.size, culled: this.culled.size, pending: this.instPending.size,
        lod: placementLodFor(activePreset()), farCull: activePreset().farCull, mode: this.renderMode, nearest,
        // Placement colliders (2026-09-23): live statics, how many wear the cooked proxy, proxies known per key.
        colliders: this.colliderOn.size, colliderProxies: proxyCols, colliderBoxes: this.colliderOn.size - proxyCols,
        proxyKeys: this.proxies.size, colliderMax: PLACEMENT_COLLIDER_MAX,
        // Interior shells (slice C): the placements a shell hid, and one placement's state by id.
        hidden: [...this.renderHidden], stateOf: (id: string) => this.placementState(String(id)),
      };
    };

    if (!devGated()) return; // everything below is the dev-gated editor UI/input
    this.canvas = app.renderer.native.domElement as HTMLCanvasElement;

    // Phase 1: keyboard (fly movement + F toggle)
    window.addEventListener('keydown', (e) => this.onKey(e, true));
    window.addEventListener('keyup', (e) => this.onKey(e, false));

    // Phase 2 + 3: B (palette), Delete/Backspace (delete), Escape/1/2/3/X (gizmo)
    this.keyHandler2 = (e: KeyboardEvent) => this.onKey2(e);
    window.addEventListener('keydown', this.keyHandler2);

    // Phase 2: left-click to place/select (only when pointer-locked in fly mode)
    this.mouseHandler = (e: MouseEvent) => this.onClick(e);
    this.canvas.addEventListener('mousedown', this.mouseHandler);

    // Phase 2: crosshair DOM element
    this.buildCrosshair();

    // Runs AFTER AtlasInterpolate (Animation/36) so localBody is ours to drive.
    app.world.addSystem(makeSystem('AtlasAdminFly', Phase.Animation, 40, (dt) => this.step(dt)));
  }

  // ─── Phase 1: keyboard + fly step ──────────────────────────────────────────

  private onKey(e: KeyboardEvent, down: boolean): void {
    const target = e.target as HTMLElement | null;
    if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) return;
    const code = e.code;
    if (atlas.editMode) return; // the unified Edit Mode owns the camera + keys
    // F toggles the fly cam only once the editor is ENGAGED (palette/outliner
    // opened this session) or to EXIT fly; otherwise plain F is the wilds
    // STRIKE key — on every dev-gated client (the user's own localhost play)
    // F parked the avatar instead of swinging (combat probe, 2026-09-04).
    // Entering fly without the editor: `/fly` in the debug console or the
    // debug menu toggle.
    if (down && code === 'KeyF') {
      if (atlas.flyMode || this.engaged) atlas.flyMode = !atlas.flyMode;
      return;
    }
    if (down) this.keys.add(code); else this.keys.delete(code);
  }

  private step(dt: number): void {
    if (atlas.editMode) { this.switchMode('individual'); return; } // Edit Mode flies the body; keep the pickable rep
    // Wave-3 representation swap: individual meshes while editing (so raycast/gizmo/
    // outliner work on real Object3Ds), instanced otherwise. Editor sessions only —
    // this system is registered behind the dev gate; other clients stay instanced.
    const wantEdit = atlas.flyMode || atlas.editorCursorMode || this.selectedId !== null;
    this.switchMode(wantEdit ? 'individual' : 'instanced');

    // Update crosshair visibility each frame — hidden in cursor-edit mode
    if (this.crosshair) {
      this.crosshair.style.display =
        atlas.flyMode && !this.paletteVisible && !atlas.editorCursorMode ? 'block' : 'none';
    }
    // Keep selection wireframe in sync with any animated object
    if (this.selectionHelper) (this.selectionHelper as THREE.BoxHelper).update();

    // React to fly-mode transitions (pointer lock + on-screen hint).
    if (atlas.flyMode !== this.prevFly) {
      this.prevFly = atlas.flyMode;
      if (atlas.flyMode) {
        try { this.canvas?.requestPointerLock(); } catch { /* ignore */ }
        this.showHint(true);
      } else {
        this.showHint(false);
        this.clearSelection();
      }
    }

    const body = atlas.localMesh;
    if (!atlas.flyMode || !body) return;

    // Phase 3: suppress WASD movement while the gizmo/panel is active so the
    // user can interact with the cursor freely without flying away.
    if (atlas.editorCursorMode) return;

    const yaw = atlas.localYaw;
    body.rotation.y = yaw; // interpolate() skipped this while flying
    const cam = body.children[0] as THREE.Object3D | undefined;
    const pitch = cam ? cam.rotation.x : 0;
    const cp = Math.cos(pitch);
    this.fwd.set(-cp * Math.sin(yaw), Math.sin(pitch), -cp * Math.cos(yaw));
    this.right.set(Math.cos(yaw), 0, -Math.sin(yaw));

    this.move.set(0, 0, 0);
    const k = this.keys;
    if (k.has('KeyW')) this.move.add(this.fwd);
    if (k.has('KeyS')) this.move.sub(this.fwd);
    if (k.has('KeyD')) this.move.add(this.right);
    if (k.has('KeyA')) this.move.sub(this.right);
    if (k.has('Space') || k.has('KeyE')) this.move.add(this.up);
    if (k.has('ControlLeft') || k.has('KeyQ')) this.move.sub(this.up);

    if (this.move.lengthSq() > 0) {
      this.move.normalize();
      const speed = (k.has('ShiftLeft') || k.has('ShiftRight')) ? FLY_BOOST : FLY_SPEED;
      body.position.addScaledVector(this.move, speed * dt);
    }
  }

  private showHint(on: boolean): void {
    if (on) {
      if (!this.hint) {
        const h = document.createElement('div');
        h.textContent =
          'FLY MODE — WASD move · Space/Ctrl up-down · Shift boost · mouse look · B palette · F to exit';
        h.style.cssText =
          'position:fixed;top:8px;left:50%;transform:translateX(-50%);z-index:9998;' +
          'background:color-mix(in srgb, var(--ax-bg) 82%, transparent);color:var(--ax-hi);border:1px solid var(--ax-line2);border-radius:var(--ax-radius);' +
          'padding:5px 12px;font:12px/1.3 var(--ax-mono);pointer-events:none';
        document.body.appendChild(h);
        this.hint = h;
      }
      this.hint.style.display = 'block';
    } else if (this.hint) {
      this.hint.style.display = 'none';
    }
  }

  // ─── Phase 2: subscription + render ────────────────────────────────────────

  private subscribe(): void {
    if (this.subscribed) return;
    const conn = atlas.conn;
    if (!conn) return;
    this.subscribed = true;

    conn
      .subscriptionBuilder()
      .onApplied(() => this.loadExisting())
      // Subscribe to all rows (the Commons world id is an autoInc value, not 0, so a
      // world_id filter can't be built before it's known); RENDERING is world-scoped
      // in addIndividual/addInstanced and rebuilt on every world hop.
      .subscribe(['SELECT * FROM admin_placement']);

    const tbl = (conn.db as unknown as { adminPlacement: AdminTable }).adminPlacement;
    tbl.onInsert((_ctx, row) => { void this.onInsert(row); });
    tbl.onUpdate((_ctx, _old, row) => { this.onUpdate(row); });
    tbl.onDelete((_ctx, row) => { this.onDelete(row); });
    // Placements are world-scoped: entering a world swaps the visible set (the bug
    // this fixes: Commons placements were persisting into every other world).
    bus.on('atlas:world-entered', () => this.rebuildForWorld());
  }

  /** Despawn every rendered placement and re-render the cache for the CURRENT world. */
  private rebuildForWorld(): void {
    if (this.selectedId !== null) this.clearSelection();
    for (const [key, obj] of [...this.placements]) {
      this.placementGroup.remove(obj);
      const assetId = obj.userData.assetId as bigint | undefined;
      if (assetId && assetId !== 0n) this.assets.dispose(assetId);
      this.placements.delete(key);
    }
    for (const key of [...this.instHandles.keys()]) this.removeInstanced(key);
    for (const row of this.rowCache.values()) {
      if (this.renderMode === 'individual') void this.addIndividual(row, false);
      else void this.addInstanced(row, false);
    }
    this.syncLampAnchors(); // the visible world changed → re-scope the admin lamp set
  }

  /** On first subscribe / reconnect, render any rows already in the local cache. */
  private loadExisting(): void {
    const conn = atlas.conn;
    if (!conn) return;
    const tbl = (conn.db as unknown as { adminPlacement: AdminTable }).adminPlacement;
    for (const row of tbl.iter()) {
      void this.onInsert(row);
    }
  }

  private async onInsert(row: AdminPlacementRow): Promise<void> {
    this.rowCache.set(row.placementId.toString(), row);
    this.scheduleLampSync();
    if (this.renderMode === 'individual') return this.addIndividual(row, true);
    return this.addInstanced(row, true);
  }

  /**
   * Recompute the admin-placement lamp set for the CURRENT world (idempotent —
   * registerLamps replaces the whole 'admin' source). Head ≈ base + 85% of the
   * ~1.9 m normalized lamp GLB × the row's Y scale.
   */
  /** Debounced lamp re-scope: onInsert fires PER ROW during the initial sync
   *  (2,600+ placements), and syncLampAnchors walks the WHOLE rowCache — the
   *  per-insert call made startup O(N²) (the tab-freeze bug). One trailing
   *  sync per burst is identical in outcome and ~2,600× cheaper. */
  private lampSyncTimer: number | null = null;
  private scheduleLampSync(): void {
    if (this.lampSyncTimer !== null) return;
    this.lampSyncTimer = window.setTimeout(() => {
      this.lampSyncTimer = null;
      this.syncLampAnchors();
    }, 120);
  }

  private syncLampAnchors(): void {
    const anchors: LampAnchor[] = [];
    for (const row of this.rowCache.values()) {
      if (row.worldId !== atlas.currentWorldId) continue;
      if (!LAMP_KEYS.has(row.prefabKey)) continue;
      anchors.push({ x: row.posX, y: row.posY + 1.6 * row.scaleY, z: row.posZ });
    }
    registerLamps('admin', anchors);
  }

  /**
   * INDIVIDUAL representation (editor mode): one Object3D per placement — what the
   * raycast/gizmo/outliner operate on. `fromServer` gates the Phase-4 undo/outliner
   * finalize so representation rebuilds (switchMode) never consume undo credits.
   */
  private async addIndividual(row: AdminPlacementRow, fromServer: boolean): Promise<void> {
    const conn = atlas.conn;
    if (!conn) return;
    const key = row.placementId.toString();
    if (this.placements.has(key)) return; // already rendered
    // World-scoped render: a Commons placement must not appear in a personal
    // world (rows stay in rowCache; a world hop rebuilds the visible set).
    if (row.worldId !== atlas.currentWorldId) return;

    // Resolve prefabKey → renderAsset via the prefab_model catalog
    const pm = [...conn.db.prefabModel.iter()].find((p) => p.prefabKey === row.prefabKey);
    if (!pm || pm.assetId === 0n) {
      // No model registered for this prefab; render a placeholder cube so the
      // placement is still visible in the editor.
      const geo = new THREE.BoxGeometry(1, 1, 1);
      const mat = new THREE.MeshStandardMaterial({ color: 0xff6633, wireframe: true });
      const cube = new THREE.Mesh(geo, mat);
      cube.position.set(row.posX, row.posY, row.posZ);
      cube.quaternion.set(row.rotX, row.rotY, row.rotZ, row.rotW);
      cube.scale.set(row.scaleX, row.scaleY, row.scaleZ);
      cube.userData.placementId = row.placementId;
      cube.userData.assetId = 0n;
      // Phase 4: store prefabKey + kind for duplicate/copy
      cube.userData.prefabKey = row.prefabKey;
      cube.userData.kind = row.kind;
      this.placementGroup.add(cube);
      this.placements.set(key, cube);
      // Phase 4: undo tracking + outliner refresh (done after the early return too)
      if (fromServer) this.onInsertFinalize(row.placementId, row.placedBy);
      return;
    }

    const tmpl = await this.assets.load(pm.assetId);
    // Concurrent guard: another insert (or loadExisting) may have won the race —
    // or the representation switched back to instanced while we were loading.
    if (this.placements.has(key) || this.renderMode !== 'individual') { this.assets.dispose(pm.assetId); return; }

    const inst = this.seatedClone(tmpl, row.prefabKey);
    inst.position.set(row.posX, row.posY, row.posZ);
    inst.quaternion.set(row.rotX, row.rotY, row.rotZ, row.rotW);
    inst.scale.set(row.scaleX, row.scaleY, row.scaleZ);
    inst.userData.placementId = row.placementId;
    inst.userData.assetId = pm.assetId;
    // Phase 4: store prefabKey + kind for duplicate/copy
    inst.userData.prefabKey = row.prefabKey;
    inst.userData.kind = row.kind;
    if (this.renderHidden.has(key)) inst.visible = false; // an interior shell stands in its place
    this.placementGroup.add(inst);
    this.placements.set(key, inst);

    // Phase 4: undo tracking + outliner refresh
    if (fromServer) this.onInsertFinalize(row.placementId, row.placedBy);
  }

  /** Structure seating law (lib/structure_pivot.ts): a `structure:*` placement is
   *  seated by its measured bbox bottom-center — the clone hangs in a holder
   *  offset by the natural-scale footprint offset under a root that takes the
   *  row transform. Other kinds keep their authored pivot (nothing old moves). */
  private seatedClone(tmpl: THREE.Object3D, prefabKey: string): THREE.Object3D {
    const inst = tmpl.clone();
    const off = seatingOffset(prefabKey, new THREE.Box3().setFromObject(tmpl));
    if (!off) return inst;
    const holder = new THREE.Group(); holder.position.set(off.x, off.y, off.z); holder.add(inst);
    const root = new THREE.Group(); root.add(holder);
    return root;
  }

  /**
   * INSTANCED representation (play mode): every submesh of the master joins the
   * shared StaticInstancer (matrix = rowTransform × part.local) — one draw call per
   * (geometry, material) across ALL placements, per the r182 per-object-UBO finding.
   */
  private async addInstanced(row: AdminPlacementRow, fromServer: boolean): Promise<void> {
    const conn = atlas.conn;
    if (!conn || !this.instancer) return;
    const key = row.placementId.toString();
    if (this.instHandles.has(key)) return; // already rendered
    if (row.worldId !== atlas.currentWorldId) return; // world-scoped render (see addIndividual)
    if (this.renderHidden.has(key)) return; // an interior shell stands in its place (setPlacementHidden restores it)
    // Play-mode distance cull: beyond the tier's object draw distance the row
    // is not instanced at all; the cull tick brings it in as the player nears.
    if (!placementWanted(Math.hypot(row.posX - atlas.localX, row.posZ - atlas.localZ), activePreset().farCull, false)) {
      this.culled.add(key);
      if (fromServer) this.onInsertFinalize(row.placementId, row.placedBy);
      return;
    }
    this.culled.delete(key);

    const rowM = new THREE.Matrix4().compose(
      new THREE.Vector3(row.posX, row.posY, row.posZ),
      new THREE.Quaternion(row.rotX, row.rotY, row.rotZ, row.rotW),
      new THREE.Vector3(row.scaleX, row.scaleY, row.scaleZ),
    );

    const pm = [...conn.db.prefabModel.iter()].find((p) => p.prefabKey === row.prefabKey);
    if (!pm || pm.assetId === 0n) {
      // Unregistered prefab → instanced placeholder box (one shared geometry/material
      // pair, so ALL placeholders are still a single draw call).
      if (!this.placeholderGeo) this.placeholderGeo = new THREE.BoxGeometry(1, 1, 1);
      if (!this.placeholderMat) this.placeholderMat = new THREE.MeshStandardMaterial({ color: 0xff6633, wireframe: true });
      const h = this.instancer.add(this.placeholderGeo, this.placeholderMat, rowM);
      this.instHandles.set(key, h >= 0 ? [h] : []);
      if (fromServer) this.onInsertFinalize(row.placementId, row.placedBy);
      return;
    }

    // The tier's placement LOD (a prop falls back to its nearest lower level).
    const lod = placementLodFor(activePreset());
    this.instPending.add(key);
    let rep: { object: THREE.Object3D; release: () => void };
    try { rep = await this.assets.loadRepresentation(pm.assetId, lod); } catch { this.instPending.delete(key); return; }
    this.instPending.delete(key);
    if (this.instHandles.has(key) || this.renderMode !== 'instanced' || row.worldId !== atlas.currentWorldId || this.renderHidden.has(key)) { rep.release(); return; } // (hidden while it loaded: the shell stands in its place)
    const master = rep.object;
    master.updateMatrixWorld(true);
    const handles: number[] = [];
    const m = new THREE.Matrix4();
    // Structure seating law (lib/structure_pivot.ts): row × footprint offset × part.
    const bb = new THREE.Box3().setFromObject(master);
    const seat = seatingOffset(row.prefabKey, bb);
    const offM = seat ? new THREE.Matrix4().makeTranslation(seat.x, seat.y, seat.z) : null;
    for (const part of collectMeshParts(master)) {
      m.copy(rowM);
      if (offM) m.multiply(offM);
      m.multiply(part.local);
      const h = this.instancer.add(part.geometry, part.material, m);
      if (h >= 0) handles.push(h);
    }
    this.instHandles.set(key, handles);
    this.instReleases.set(key, rep.release); // hold the representation while instanced
    this.instLod.set(key, lod);
    // The collider plans from the SAME bbox the seat was measured on (the PIVOT law, both readers).
    this.bboxByKey.set(row.prefabKey, { min: { x: bb.min.x, y: bb.min.y, z: bb.min.z }, max: { x: bb.max.x, y: bb.max.y, z: bb.max.z } });
    this.assetByKey.set(row.prefabKey, pm.assetId);
    void this.ensureProxy(row.prefabKey, pm.assetId);
    this.syncCollider(key, row);
    if (fromServer) this.onInsertFinalize(row.placementId, row.placedBy);
  }

  /** Remove one placement's instanced parts + release its asset ref (and its collider — it follows the instance). */
  private removeInstanced(key: string): void {
    this.dropCollider(key);
    const handles = this.instHandles.get(key);
    if (handles && this.instancer) for (const h of handles) this.instancer.remove(h);
    this.instHandles.delete(key);
    const rel = this.instReleases.get(key);
    if (rel) { rel(); this.instReleases.delete(key); }
    this.instLod.delete(key);
  }

  // ── Placement colliders (2026-09-23) ─────────────────────────────────────────

  private physics(): PhysicsModule | null {
    if (this.physicsRef === undefined) this.physicsRef = (this.appRef?.getModule('PhysicsModule') as PhysicsModule | undefined) ?? null;
    return this.physicsRef;
  }

  /** Fetch a prefab's cooked collider proxy ONCE per key (fire-and-forget: the loading
   *  gate never waits on it — a box stands in until it lands, then the next cull tick
   *  upgrades the standing boxes). A missing `asset_collider` row is re-asked later:
   *  the catalog subscription (StructureRender / Settlements) may still be landing. */
  private async ensureProxy(prefabKey: string, assetId: bigint): Promise<void> {
    if (this.proxies.has(prefabKey) || this.proxyLoads.has(prefabKey)) return;
    const conn = atlas.conn;
    if (!conn) return;
    const col = conn.db.assetCollider.assetId.find(assetId);
    if (!col) {
      const at = this.proxyRetryAt.get(prefabKey);
      if (at !== undefined && at > performance.now()) return;
      this.proxyRetryAt.set(prefabKey, performance.now() + PLACEMENT_PROXY_RETRY_MS);
      return;
    }
    this.proxyLoads.add(prefabKey);
    try {
      const p = (await (await fetch(`${ASSET_STORE_BASE}/${col.colliderUri}`)).json()) as ColliderProxy;
      let usable: ColliderProxy | null = p.positions?.length || p.points?.length || p.halfExtents ? p : null;
      if (usable && usable.shape === 'trimesh_static' && usable.positions && PLACEMENT_HULL_KEY_RE.test(prefabKey)) usable = hullOf(usable.positions);
      this.proxies.set(prefabKey, usable);
    } catch (e) {
      console.warn(`[AtlasAdminEditor] collider proxy for ${prefabKey} failed — its placements keep box colliders`, e);
      this.proxies.set(prefabKey, null);
    } finally { this.proxyLoads.delete(prefabKey); }
  }

  /** Bring one instanced placement's static up to date: inside the walking ring
   *  and solid → the proxy at the render's frame (or a box), else nothing. */
  private syncCollider(key: string, row: AdminPlacementRow): void {
    const physics = this.physics();
    if (!physics) return;
    const bb = this.bboxByKey.get(row.prefabKey);
    if (!bb) return;
    const live = this.colliderOn.get(key);
    const d = Math.hypot(row.posX - atlas.localX, row.posZ - atlas.localZ);
    if (this.colliderHidden.has(key) || !colliderRingWant(d, live !== undefined)) { if (live) this.dropCollider(key); return; }
    // A key whose `asset_collider` row was not there yet is re-asked (ensureProxy paces the retry).
    const aid = this.assetByKey.get(row.prefabKey);
    if (aid !== undefined && !this.proxies.has(row.prefabKey)) void this.ensureProxy(row.prefabKey, aid);
    const proxy = this.proxies.get(row.prefabKey) ?? null;
    const plan = placementColliderPlan(
      row.prefabKey,
      { x: row.posX, y: row.posY, z: row.posZ },
      { x: row.rotX, y: row.rotY, z: row.rotZ, w: row.rotW },
      { x: row.scaleX, y: row.scaleY, z: row.scaleZ },
      bb, proxy !== null,
    );
    if (!plan) { if (live) this.dropCollider(key); return; }
    if (live === plan.shape) return; // standing and up to date (a row change re-instances → re-syncs)
    if (live) this.dropCollider(key); // a box upgrading to the proxy that just landed
    if (this.colliderOn.size >= PLACEMENT_COLLIDER_MAX) {
      if (!this.colliderCapWarned) { this.colliderCapWarned = true; console.warn(`[AtlasAdminEditor] placement colliders at the cap (${PLACEMENT_COLLIDER_MAX}) — farther placements stay walk-through until the ring thins`); }
      return;
    }
    const id = `placement:${key}`;
    const ok = plan.shape === 'proxy' && proxy
      ? physics.addStaticCollider(id, proxy, plan.t)
      : physics.addStaticCollider(id, { shape: 'box', halfExtents: plan.half }, plan.t);
    if (ok) this.colliderOn.set(key, plan.shape);
    else console.warn(`[AtlasAdminEditor] placement ${key} (${row.prefabKey}): Rapier refused its ${plan.shape} collider`);
  }

  private dropCollider(key: string): void {
    if (!this.colliderOn.has(key)) return;
    this.physics()?.removeStaticCollider(`placement:${key}`);
    this.colliderOn.delete(key);
  }

  /**
   * Play-mode distance cull + LOD follow (2026-09-08): every PLACEMENT_CULL_EVERY
   * frames, instance the rows inside the tier's object draw distance and drop
   * the rest (hysteresis in placementWanted); a tier change re-instances a row
   * at the new placement LOD. The editor's individual representation shows
   * everything (an admin must be able to reach any placement).
   */
  private cullTick(): void {
    if (++this.cullFrame % PLACEMENT_CULL_EVERY !== 0 || this.renderMode !== 'instanced' || !this.instancer) return;
    if (atlas.currentWorldId === null) return;
    const preset = activePreset();
    const farCull = preset.farCull, lod = placementLodFor(preset);
    const px = atlas.localX, pz = atlas.localZ;
    for (const [key, row] of this.rowCache) {
      if (row.worldId !== atlas.currentWorldId) continue;
      if (this.instPending.has(key)) continue;
      if (this.renderHidden.has(key)) continue; // hidden by an interior shell — neither instanced nor culled
      let isIn = this.instHandles.has(key);
      if (isIn && this.instLod.get(key) !== lod) { this.removeInstanced(key); this.culled.add(key); isIn = false; }
      const want = placementWanted(Math.hypot(row.posX - px, row.posZ - pz), farCull, isIn);
      if (want && !isIn) { this.culled.delete(key); void this.addInstanced(row, false); }
      else if (!want && isIn) { this.removeInstanced(key); this.culled.add(key); }
      else if (isIn) this.syncCollider(key, row); // the walking ring moves with the player; a landed proxy upgrades its boxes
    }
  }

  /** Swap between the play (instanced) and editor (individual) representations. */
  private switchMode(mode: 'instanced' | 'individual'): void {
    if (this.renderMode === mode) return;
    this.renderMode = mode;
    if (mode === 'individual') {
      this.culled.clear(); // the editor shows every placement
      for (const key of [...this.instHandles.keys()]) this.removeInstanced(key);
      for (const row of this.rowCache.values()) void this.addIndividual(row, false);
    } else {
      for (const [key, obj] of [...this.placements]) {
        this.placementGroup.remove(obj);
        const assetId = obj.userData.assetId as bigint | undefined;
        if (assetId && assetId !== 0n) this.assets.dispose(assetId);
        this.placements.delete(key);
      }
      for (const row of this.rowCache.values()) void this.addInstanced(row, false);
    }
  }

  /**
   * Phase 4 finalization shared between the placeholder and full-asset paths.
   * Pushes a `place` undo entry for any pending local adminPlaceObject calls
   * and refreshes the outliner if it is visible.
   */
  /** SDK reducer calls resolve on Ok and REJECT with the SenderError on Err — a
   *  synchronous try/catch around them never saw a refusal (the bug sweep), so every
   *  recovery path below was dead. `onFail` runs on the rejection. */
  private sent(p: Promise<void> | void, what: string, onFail?: () => void): void {
    Promise.resolve(p).catch((err: unknown) => { console.warn(`[AtlasAdminEditor] ${what} failed:`, err); onFail?.(); });
  }
  /** A place was refused: its onInsert will never come, so release its pending undo slot. */
  private readonly unpend = (): void => { this._pendingUndoCount = Math.max(0, this._pendingUndoCount - 1); };

  private onInsertFinalize(placementId: bigint, placedBy?: { isEqual(other: unknown): boolean }): void {
    // Only MY inserts settle my pending entries: every admin_placement row this client
    // sees lands here, so another admin's placement used to be claimed as mine — and a
    // later Ctrl+Z deleted an object this client never placed.
    const mine = !placedBy || !atlas.conn?.identity || placedBy.isEqual(atlas.conn.identity);
    if (mine && this._pendingUndoCount > 0) {
      this._pendingUndoCount--;
      this.undoStack.push({ kind: 'place', placementId });
      if (this.undoStack.length > 50) this.undoStack.shift();
    }
    if (this.outlinerVisible) this.refreshOutliner();
  }

  private onUpdate(row: AdminPlacementRow): void {
    const key = row.placementId.toString();
    this.rowCache.set(key, row);
    this.scheduleLampSync();
    if (this.renderMode === 'individual') {
      const inst = this.placements.get(key);
      if (!inst) return;
      inst.position.set(row.posX, row.posY, row.posZ);
      inst.quaternion.set(row.rotX, row.rotY, row.rotZ, row.rotW);
      inst.scale.set(row.scaleX, row.scaleY, row.scaleZ);
    } else {
      // Rare outside an editor session — rebuild this row's instanced parts.
      this.removeInstanced(key);
      void this.addInstanced(row, false);
    }
  }

  private onDelete(row: AdminPlacementRow): void {
    const key = row.placementId.toString();
    this.rowCache.delete(key);
    this.culled.delete(key);
    this.scheduleLampSync();
    if (this.renderMode === 'individual') {
      const inst = this.placements.get(key);
      if (!inst) return;
      // If the deleted placement is selected, clear selection first
      if (this.selectedId === row.placementId) this.clearSelection();
      this.placementGroup.remove(inst);
      const assetId = inst.userData.assetId as bigint | undefined;
      if (assetId && assetId !== 0n) this.assets.dispose(assetId);
      this.placements.delete(key);
    } else {
      this.removeInstanced(key);
    }
    // Phase 4: refresh outliner
    if (this.outlinerVisible) this.refreshOutliner();
  }

  // ─── Phase 2: click to place / select ─────────────────────────────────────

  private onClick(e: MouseEvent): void {
    if (e.button !== 0) return;
    if (atlas.editMode) return; // the unified Edit Mode owns the pointer (it flies + pointer-locks too — the legacy placer used to fire underneath it, 2026-09-12)
    if (!atlas.flyMode || document.pointerLockElement !== this.canvas) return;

    const body = atlas.localMesh;
    if (!body) return;
    const cam = body.children[0] as THREE.Camera | undefined;
    if (!cam) return;

    // Center-screen ray (works with pointer lock — Minecraft-style aim + click)
    this.raycaster.setFromCamera(new THREE.Vector2(0, 0), cam);

    // 1. Check existing placements first (select)
    const placementMeshes = [...this.placements.values()];
    const hits = this.raycaster.intersectObjects(placementMeshes, true);
    if (hits.length > 0) {
      // Walk up the hierarchy to the root child of placementGroup
      let obj: THREE.Object3D = hits[0].object;
      while (obj.parent && obj.parent !== this.placementGroup) obj = obj.parent;
      if (obj.userData.placementId != null) {
        this.setSelected(obj.userData.placementId as bigint, obj);
        return;
      }
    }

    // 2. No placement hit — place new object if a brush is set
    if (!this.brushKey) return;
    const conn = atlas.conn;
    if (!conn) return;

    const commons = [...conn.db.world.iter()].find((w) => w.kind === 'commons');
    if (!commons) return;
    const seed = commons.terrainSeed;

    // Ray-march the terrain: step ~1 m at a time up to 400 m
    const ray = this.raycaster.ray;
    const p = ray.origin.clone();
    let placed = false;
    for (let i = 0; i < 400; i++) {
      p.addScaledVector(ray.direction, 1);
      // meshHeight, not terrainHeight (high-relief audit 2026-08-05): the
      // analytic field sits above the rendered mesh mid-cell on slopes, so
      // ray-marched placements landed visibly airborne on steep ground.
      const gy = meshHeight(seed, p.x, p.z);
      if (p.y <= gy) {
        this._pendingUndoCount++; // the id arrives via onInsert (mine only); a refusal releases the slot
        this.sent((conn.reducers as unknown as AdminReducers).adminPlaceObject({
          worldId: atlas.commonsWorldId ?? 0n, // the REAL Commons id (autoInc), NOT the 0n sentinel
          prefabKey: this.brushKey,
          kind: this.brushKind,
          posX: p.x,
          posY: gy,
          posZ: p.z,
          rotX: 0,
          rotY: 0,
          rotZ: 0,
          rotW: 1,
          scaleX: 1,
          scaleY: 1,
          scaleZ: 1,
        }), 'adminPlaceObject', this.unpend);
        placed = true;
        break;
      }
    }
    if (!placed) {
      console.info('[AtlasAdminEditor] no terrain hit within 400 m');
    }
  }

  private setSelected(id: bigint, obj: THREE.Object3D): void {
    this.clearSelection();
    this.selectedId = id;
    this.gizmoTarget = obj;

    // Phase 4: snapshot the current transform for undo — updated after each
    // commitTransform so successive commits each capture the correct before-state.
    this._beforeTransform = {
      posX: obj.position.x,
      posY: obj.position.y,
      posZ: obj.position.z,
      rotX: obj.quaternion.x,
      rotY: obj.quaternion.y,
      rotZ: obj.quaternion.z,
      rotW: obj.quaternion.w,
      scaleX: obj.scale.x,
      scaleY: obj.scale.y,
      scaleZ: obj.scale.z,
    };

    // BoxHelper highlight
    const helper = new THREE.BoxHelper(obj, 0x00ff88);
    this.scene?.add(helper);
    this.selectionHelper = helper;

    // Phase 3: enter cursor-edit mode — free the OS cursor so the gizmo and
    // numeric panel can receive pointer events.
    document.exitPointerLock();
    atlas.editorCursorMode = true;

    // Attach the gizmo (created lazily on first select)
    this.ensureGizmo();
    this.gizmo?.attach(obj);
    this.gizmo?.setMode('translate');

    // Show the numeric transform panel
    this.showPanel();
  }

  private clearSelection(): void {
    this.selectedId = null;
    this.gizmoTarget = null;

    // Phase 3 cleanup — must happen before editorCursorMode is cleared so any
    // downstream consumers can still see the flag if needed.
    this.gizmo?.detach();
    this.hidePanel();
    atlas.editorCursorMode = false;

    // Phase 2: remove the selection BoxHelper
    if (this.selectionHelper) {
      this.scene?.remove(this.selectionHelper);
      // BoxHelper extends LineSegments; dispose the underlying geometry
      const helper = this.selectionHelper as unknown as { geometry?: { dispose(): void } };
      helper.geometry?.dispose();
      this.selectionHelper = null;
    }
  }

  // ─── Phase 2 + 4: key handler (palette, delete, gizmo, undo, outliner) ────────

  private onKey2(e: KeyboardEvent): void {
    // Always guard against typing in input fields (Phase 1 convention).
    const target = e.target as HTMLElement | null;
    if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) return;
    if (atlas.editMode) return; // Edit Mode owns Ctrl+Z / 1-3 / X / Delete — they double-fired here (2026-09-12)
    if (atlas.spaceFlight) return; // the editor edits the ground; in the sector O is the scan ping (space campaign B — pictured: the outliner opened over the chart)

    // ── Phase 4: keys that work regardless of fly mode ──────────────────────
    //    KeyO — outliner toggle
    //    Ctrl+Z — undo (also Cmd+Z on macOS)
    if (e.code === 'KeyO' && !e.ctrlKey && !e.metaKey && !e.shiftKey) {
      e.preventDefault();
      this.toggleOutliner();
      return;
    }
    if (e.code === 'KeyZ' && (e.ctrlKey || e.metaKey) && !e.shiftKey) {
      e.preventDefault();
      this.applyUndo();
      return;
    }

    // ── Fly-mode gate — all Phase 2/3 and remaining Phase 4 keys ────────────
    if (!atlas.flyMode) return;

    switch (e.code) {
      case 'KeyB':
        e.preventDefault();
        this.togglePalette();
        break;

      // ── Phase 4: duplicate / copy / paste ──────────────────────────────────
      case 'KeyD':
        if (e.shiftKey) { e.preventDefault(); this.duplicate(); }
        break;

      case 'KeyC':
        if (e.ctrlKey || e.metaKey) { e.preventDefault(); this.copySelection(); }
        break;

      case 'KeyV':
        if (e.ctrlKey || e.metaKey) { e.preventDefault(); this.paste(); }
        break;

      // ── Phase 2: delete ────────────────────────────────────────────────────
      case 'Delete':
      case 'Backspace':
        if (this.selectedId != null) {
          e.preventDefault();
          const conn = atlas.conn;
          if (!conn) break;
          this.sent((conn.reducers as unknown as AdminReducers).adminDeleteObject({ placementId: this.selectedId }), 'adminDeleteObject');
          this.clearSelection();
        }
        break;

      // ── Phase 3 keys ────────────────────────────────────────────────────────
      case 'Escape':
        if (this.selectedId != null) {
          e.preventDefault();
          this.clearSelection();
        }
        break;

      case 'Digit1':
        if (this.gizmo) { e.preventDefault(); this.gizmo.setMode('translate'); }
        break;

      case 'Digit2':
        if (this.gizmo) { e.preventDefault(); this.gizmo.setMode('rotate'); }
        break;

      case 'Digit3':
        if (this.gizmo) { e.preventDefault(); this.gizmo.setMode('scale'); }
        break;

      case 'KeyX':
        if (this.gizmo) {
          e.preventDefault();
          this.snapEnabled = !this.snapEnabled;
          this.applySnap();
        }
        break;

      default:
        break;
    }
  }

  // ─── Phase 3: TransformControls gizmo ──────────────────────────────────────

  /**
   * Lazily creates the TransformControls instance on first use.
   *
   * Cast note: TransformControls constructor signature (from @types/three) takes
   * `Camera` imported from "three". The fly camera is an Object3D child of
   * atlas.localMesh; we know it is a PerspectiveCamera at runtime. Both "three"
   * and "three/webgpu" re-export Camera from Three.Core.d.ts so the types are
   * structurally identical — the `as unknown as import('three').Camera` cast is
   * only required to satisfy the module-identity boundary between the two import
   * specifiers.
   */
  private ensureGizmo(): void {
    if (this.gizmo) return;
    const body = atlas.localMesh;
    if (!body || !this.scene || !this.canvas) return;

    // The fly camera is placed as the first child of the local body by AtlasAvatarModule.
    const flyCam = body.children[0] as unknown as import('three').Camera;

    const gizmo = new TransformControls(flyCam, this.canvas);

    // Live-update the numeric panel while the user drags a gizmo handle.
    gizmo.addEventListener('objectChange', () => { this.refreshPanel(); });

    // Persist to the server only on drag-END (not every frame), to avoid flooding
    // the reducer.  The `dragging-changed` event carries `value: true` on start
    // and `value: false` on end; we commit only on the false (end) transition.
    gizmo.addEventListener('dragging-changed', (e) => {
      if (!e.value && this.gizmoTarget) {
        this.commitTransform(this.gizmoTarget);
      }
    });

    // r182 Controls-based API: add the HELPER to the scene, not the gizmo itself.
    const helper = gizmo.getHelper();
    this.scene.add(helper);

    this.gizmo = gizmo;
    this.gizmoHelper = helper;
  }

  /** Apply or clear snapping on all three gizmo axes. */
  private applySnap(): void {
    if (!this.gizmo) return;
    if (this.snapEnabled) {
      this.gizmo.setTranslationSnap(1); // 1 m grid
      this.gizmo.setRotationSnap(THREE.MathUtils.degToRad(15)); // 15° steps
      this.gizmo.setScaleSnap(0.25); // 0.25 increments
    } else {
      this.gizmo.setTranslationSnap(null);
      this.gizmo.setRotationSnap(null);
      this.gizmo.setScaleSnap(null);
    }
  }

  // ─── Phase 3: numeric transform panel ──────────────────────────────────────

  /**
   * Build the transform panel DOM once and wire input events.
   * Subsequent calls to showPanel()/hidePanel() just toggle display.
   */
  private buildTransformPanel(): void {
    if (this.transformPanel) return;

    const panel = document.createElement('div');
    panel.style.cssText =
      'position:fixed;bottom:60px;left:12px;z-index:9999;' +
      'background:color-mix(in srgb, var(--ax-bg) 92%, transparent);color:var(--ax-text);font:12px/1.5 var(--ax-mono);' +
      'padding:10px 14px;border:1px solid var(--ax-line2);border-radius:var(--ax-radius);width:300px;' +
      'box-shadow:0 4px 20px rgba(0,0,0,.8);display:none';

    // Header
    const header = document.createElement('div');
    header.textContent = 'Transform  [ selected ]';
    header.style.cssText = 'font-weight:700;color:var(--ax-hi);margin-bottom:8px;font-size:13px';
    panel.appendChild(header);

    // Shared field style
    const fldStyle =
      'width:62px;background:var(--ax-bg2);color:var(--ax-text);border:1px solid var(--ax-line2);' +
      'border-radius:var(--ax-radius);padding:2px 5px;font:11px var(--ax-mono);text-align:right;' +
      'box-sizing:border-box';

    const makeFld = (): HTMLInputElement => {
      const inp = document.createElement('input');
      inp.type = 'number';
      inp.step = 'any';
      inp.style.cssText = fldStyle;
      return inp;
    };

    const posX = makeFld(), posY = makeFld(), posZ = makeFld();
    const rotX = makeFld(), rotY = makeFld(), rotZ = makeFld();
    const scX  = makeFld(), scY  = makeFld(), scZ  = makeFld();
    const scU  = makeFld();

    this.panelInputs = { posX, posY, posZ, rotX, rotY, rotZ, scX, scY, scZ, scU };

    // Helper to build a labelled row
    const addRow = (
      sectionLabel: string,
      axisLabels: string[],
      fields: HTMLInputElement[],
    ): void => {
      const row = document.createElement('div');
      row.style.cssText = 'display:flex;align-items:center;gap:3px;margin-bottom:3px';
      const sl = document.createElement('span');
      sl.textContent = sectionLabel;
      sl.style.cssText = 'color:var(--ax-hi);width:72px;flex-shrink:0;font-size:11px';
      row.appendChild(sl);
      axisLabels.forEach((ax, i) => {
        const al = document.createElement('span');
        al.textContent = ax;
        al.style.cssText = 'color:var(--ax-muted);flex-shrink:0;font-size:10px';
        row.appendChild(al);
        row.appendChild(fields[i]);
      });
      panel.appendChild(row);
    };

    addRow('Position', ['X:', 'Y:', 'Z:'], [posX, posY, posZ]);
    addRow('Rotation°', ['X:', 'Y:', 'Z:'], [rotX, rotY, rotZ]);
    addRow('Scale', ['X:', 'Y:', 'Z:'], [scX, scY, scZ]);

    // Uniform-scale convenience row
    const urow = document.createElement('div');
    urow.style.cssText = 'display:flex;align-items:center;gap:3px;margin-bottom:6px';
    const ul = document.createElement('span');
    ul.textContent = 'Uniform:';
    ul.style.cssText = 'color:var(--ax-hi);width:72px;flex-shrink:0;font-size:11px';
    urow.appendChild(ul);
    urow.appendChild(scU);
    panel.appendChild(urow);

    // Key-hints footer
    const hintDiv = document.createElement('div');
    hintDiv.textContent = '1=move  2=rotate  3=scale  X=snap  Esc=done';
    hintDiv.style.cssText = 'color:var(--ax-on-accent);font-size:10px';
    panel.appendChild(hintDiv);

    document.body.appendChild(panel);
    this.transformPanel = panel;

    // ── Wire change events ───────────────────────────────────────────────────
    // We use 'change' (fires on Enter or blur) rather than 'input' (every keystroke)
    // so the reducer is called only when the user finishes editing a field — this
    // avoids flooding adminTransformObject while the user is mid-type.

    const applyPanel = (): void => {
      const obj = this.gizmoTarget;
      if (!obj) return;
      const d2r = THREE.MathUtils.degToRad;
      obj.position.set(
        parseFloat(posX.value) || 0,
        parseFloat(posY.value) || 0,
        parseFloat(posZ.value) || 0,
      );
      // Euler order matches Three.js default ('XYZ')
      obj.rotation.set(
        d2r(parseFloat(rotX.value) || 0),
        d2r(parseFloat(rotY.value) || 0),
        d2r(parseFloat(rotZ.value) || 0),
      );
      obj.scale.set(
        parseFloat(scX.value) || 1,
        parseFloat(scY.value) || 1,
        parseFloat(scZ.value) || 1,
      );
      this.commitTransform(obj);
    };

    const applyUniform = (): void => {
      const obj = this.gizmoTarget;
      if (!obj) return;
      const s = parseFloat(scU.value) || 1;
      obj.scale.set(s, s, s);
      scX.value = scY.value = scZ.value = s.toFixed(3);
      this.commitTransform(obj);
    };

    for (const fld of [posX, posY, posZ, rotX, rotY, rotZ, scX, scY, scZ]) {
      fld.addEventListener('change', applyPanel);
    }
    scU.addEventListener('change', applyUniform);
  }

  /** Show and populate the numeric panel from the current gizmoTarget. */
  private showPanel(): void {
    this.buildTransformPanel(); // no-op if already built
    if (this.transformPanel) this.transformPanel.style.display = 'block';
    this.refreshPanel();
  }

  /** Hide the numeric panel (does not destroy it). */
  private hidePanel(): void {
    if (this.transformPanel) this.transformPanel.style.display = 'none';
  }

  /**
   * Refresh the numeric fields from the current gizmoTarget.
   * Called on initial select AND by the gizmo's `objectChange` event so the
   * fields stay in sync while the user drags a handle.
   */
  private refreshPanel(): void {
    const inp = this.panelInputs;
    const obj = this.gizmoTarget;
    if (!inp || !obj) return;

    const r2d = THREE.MathUtils.radToDeg;
    inp.posX.value = obj.position.x.toFixed(3);
    inp.posY.value = obj.position.y.toFixed(3);
    inp.posZ.value = obj.position.z.toFixed(3);
    inp.rotX.value = r2d(obj.rotation.x).toFixed(2);
    inp.rotY.value = r2d(obj.rotation.y).toFixed(2);
    inp.rotZ.value = r2d(obj.rotation.z).toFixed(2);
    inp.scX.value = obj.scale.x.toFixed(3);
    inp.scY.value = obj.scale.y.toFixed(3);
    inp.scZ.value = obj.scale.z.toFixed(3);
    // Show uniform value only when all three axes match
    const allSame =
      Math.abs(obj.scale.x - obj.scale.y) < 1e-4 &&
      Math.abs(obj.scale.y - obj.scale.z) < 1e-4;
    inp.scU.value = allSame ? obj.scale.x.toFixed(3) : '';
  }

  /**
   * Persist the current object transform to the server via adminTransformObject.
   * Called on gizmo drag-END and numeric-field commit — NOT every frame.
   *
   * Phase 4: also pushes a `transform` undo entry with the `before` snapshot
   * captured at selection time (or after the previous commit), then updates the
   * snapshot so the next commit has the correct before-state.
   */
  private commitTransform(obj: THREE.Object3D): void {
    if (this.selectedId == null) return;
    const conn = atlas.conn;
    if (!conn) return;

    // Phase 4: push undo entry BEFORE sending to the server so the stack is
    // consistent even if the reducer call throws.
    if (this._beforeTransform) {
      this.undoStack.push({
        kind: 'transform',
        placementId: this.selectedId,
        before: this._beforeTransform,
      });
      if (this.undoStack.length > 50) this.undoStack.shift();
      // Update before → the NEW state so the next commit gets the right snapshot.
      this._beforeTransform = {
        posX: obj.position.x,
        posY: obj.position.y,
        posZ: obj.position.z,
        rotX: obj.quaternion.x,
        rotY: obj.quaternion.y,
        rotZ: obj.quaternion.z,
        rotW: obj.quaternion.w,
        scaleX: obj.scale.x,
        scaleY: obj.scale.y,
        scaleZ: obj.scale.z,
      };
    }

    const id = this.selectedId;
    const pushed = this._beforeTransform ? this.undoStack[this.undoStack.length - 1] : undefined;
    this.sent((conn.reducers as unknown as AdminReducers).adminTransformObject({
      placementId: id,
      posX: obj.position.x,
      posY: obj.position.y,
      posZ: obj.position.z,
      rotX: obj.quaternion.x,
      rotY: obj.quaternion.y,
      rotZ: obj.quaternion.z,
      rotW: obj.quaternion.w,
      scaleX: obj.scale.x,
      scaleY: obj.scale.y,
      scaleZ: obj.scale.z,
    }), 'adminTransformObject', () => this.revertTransform(id, pushed));
  }

  /** A transform the server refused: drop its undo entry (the move never happened) and put
   *  the object back where the SERVER row says it is — the mesh had already moved locally. */
  private revertTransform(id: bigint, pushed?: UndoEntry): void {
    if (pushed) { const i = this.undoStack.lastIndexOf(pushed); if (i >= 0) this.undoStack.splice(i, 1); }
    const row = this.rowCache.get(id.toString());
    if (!row) return;
    this.onUpdate(row);
    if (this.selectedId === id) {
      this._beforeTransform = {
        posX: row.posX, posY: row.posY, posZ: row.posZ,
        rotX: row.rotX, rotY: row.rotY, rotZ: row.rotZ, rotW: row.rotW,
        scaleX: row.scaleX, scaleY: row.scaleY, scaleZ: row.scaleZ,
      };
      this.refreshPanel();
    }
  }

  // ─── Phase 2: palette panel ─────────────────────────────────────────────────

  private togglePalette(): void {
    if (this.paletteVisible) this.closePalette();
    else this.openPalette();
  }

  /** The editor was used this session (palette/outliner) — F may then enter fly. */
  private engaged = false;

  private openPalette(): void {
    this.engaged = true;
    this.paletteVisible = true;
    document.exitPointerLock(); // release cursor so DOM is clickable

    if (this.palette) {
      this.palette.style.display = 'flex';
      (this.palette.querySelector('input') as HTMLInputElement | null)?.focus();
      return;
    }

    const conn = atlas.conn;
    const panel = document.createElement('div');
    panel.style.cssText =
      'position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);z-index:9999;' +
      'background:color-mix(in srgb, var(--ax-bg) 95%, transparent);color:var(--ax-text);font:13px/1.5 var(--ax-font);' +
      'padding:12px 16px;border:1px solid var(--ax-line2);border-radius:var(--ax-radius);width:360px;' +
      'max-height:62vh;overflow:hidden;display:flex;flex-direction:column;gap:8px;' +
      'box-shadow:0 8px 32px rgba(0,0,0,.8)';

    const header = document.createElement('div');
    header.style.cssText = 'font-weight:700;color:var(--ax-hi);flex-shrink:0';
    header.textContent = 'Prefab Palette  ( B to close )';

    const search = document.createElement('input');
    search.type = 'text';
    search.placeholder = 'Search prefabs…';
    search.style.cssText =
      'background:var(--ax-bg2);color:var(--ax-text);border:1px solid var(--ax-line2);border-radius:var(--ax-radius);' +
      'padding:5px 8px;font:12px var(--ax-mono);width:100%;box-sizing:border-box;flex-shrink:0';

    const brushLabel = document.createElement('div');
    brushLabel.style.cssText = 'font:11px var(--ax-mono);color:var(--ax-muted);flex-shrink:0';
    const refreshBrushLabel = (): void => {
      brushLabel.textContent = this.brushKey ? `Brush: ${this.brushKey}` : 'No brush selected';
    };
    refreshBrushLabel();

    const list = document.createElement('div');
    list.style.cssText =
      'overflow-y:auto;flex:1;display:flex;flex-direction:column;gap:2px;min-height:0';

    const footer = document.createElement('div');
    footer.style.cssText = 'display:flex;justify-content:flex-end;flex-shrink:0';
    const closeBtn = document.createElement('button');
    closeBtn.textContent = 'Close';
    closeBtn.style.cssText =
      'background:var(--ax-bg3);color:var(--ax-text);border:1px solid var(--ax-line2);border-radius:var(--ax-radius);' +
      'padding:4px 12px;cursor:pointer;font:12px var(--ax-font)';
    closeBtn.addEventListener('click', () => this.closePalette());
    footer.appendChild(closeBtn);

    panel.append(header, search, brushLabel, list, footer);
    document.body.appendChild(panel);
    this.palette = panel;

    const render = (q: string): void => {
      while (list.firstChild) list.removeChild(list.firstChild);
      if (!conn) return;
      const prefabs = [...conn.db.prefabModel.iter()]
        .filter(
          (p) =>
            p.category === 'structure' ||
            p.category === 'prop' ||
            p.prefabKey.startsWith('structure:') ||
            p.prefabKey.startsWith('prop:'),
        )
        .filter((p) => !q || p.prefabKey.toLowerCase().includes(q.toLowerCase()))
        .sort((a, b) => a.prefabKey.localeCompare(b.prefabKey));

      if (prefabs.length === 0) {
        const empty = document.createElement('div');
        empty.textContent = 'No prefabs found';
        empty.style.cssText = 'color:var(--ax-muted);padding:8px 4px;font:12px var(--ax-mono)';
        list.appendChild(empty);
        return;
      }

      for (const pm of prefabs) {
        const isSel = pm.prefabKey === this.brushKey;
        const btn = document.createElement('button');
        btn.textContent = pm.prefabKey;
        btn.style.cssText =
          `background:${isSel ? 'var(--ax-sel)' : 'var(--ax-bg2)'};` +
          `color:${isSel ? 'var(--ax-hi)' : 'var(--ax-text)'};` +
          `border:1px solid ${isSel ? 'var(--ax-accent)' : 'var(--ax-line)'};` +
          'border-radius:var(--ax-radius);padding:4px 8px;cursor:pointer;font:11px var(--ax-mono);text-align:left';
        btn.addEventListener('click', () => {
          this.brushKey = pm.prefabKey;
          this.brushKind =
            pm.category || (pm.prefabKey.startsWith('structure:') ? 'structure' : 'prop');
          refreshBrushLabel();
          this.closePalette();
        });
        list.appendChild(btn);
      }
    };

    search.addEventListener('input', () => render(search.value));
    render('');
    search.focus();
  }

  private closePalette(): void {
    this.paletteVisible = false;
    if (this.palette) this.palette.style.display = 'none';
    // Only re-request pointer lock when NOT in editor cursor mode (Phase 3).
    // In cursor-edit mode the user needs a free cursor for the gizmo + panel.
    if (!atlas.editorCursorMode) {
      try { this.canvas?.requestPointerLock(); } catch { /* ignore */ }
    }
  }

  // ─── Phase 2: crosshair ─────────────────────────────────────────────────────

  private buildCrosshair(): void {
    const h = document.createElement('div');
    h.style.cssText =
      'position:fixed;top:50%;left:50%;width:8px;height:8px;margin:-4px 0 0 -4px;' +
      'background:rgba(255,255,255,.75);border-radius:50%;z-index:9997;' +
      'display:none;pointer-events:none;box-shadow:0 0 3px rgba(0,0,0,.8)';
    document.body.appendChild(h);
    this.crosshair = h;
  }

  // ─── Phase 4: duplicate ──────────────────────────────────────────────────────

  /**
   * Duplicate the selected placement: same prefabKey/kind/rotation/scale, placed
   * 2 m to the right (+X) of the original. Pushes an undo entry on success.
   */
  private duplicate(): void {
    if (this.selectedId == null || !this.gizmoTarget) return;
    const conn = atlas.conn;
    if (!conn) return;
    const obj = this.gizmoTarget;
    const prefabKey = (obj.userData.prefabKey as string | undefined) ?? this.brushKey;
    const kind = (obj.userData.kind as string | undefined) ?? this.brushKind;
    if (!prefabKey) {
      console.info('[AtlasAdminEditor] duplicate: no prefabKey on selected object');
      return;
    }
    this._pendingUndoCount++; // the id arrives via onInsert (mine only); a refusal releases the slot
    this.sent((conn.reducers as unknown as AdminReducers).adminPlaceObject({
      worldId: atlas.commonsWorldId ?? 0n, // the REAL Commons id (autoInc), NOT the 0n sentinel
      prefabKey,
      kind,
      posX: obj.position.x + 2,
      posY: obj.position.y,
      posZ: obj.position.z,
      rotX: obj.quaternion.x,
      rotY: obj.quaternion.y,
      rotZ: obj.quaternion.z,
      rotW: obj.quaternion.w,
      scaleX: obj.scale.x,
      scaleY: obj.scale.y,
      scaleZ: obj.scale.z,
    }), 'duplicate', this.unpend);
  }

  // ─── Phase 4: copy/paste ─────────────────────────────────────────────────────

  /** Copy the selected placement's prefabKey/kind/rotation/scale to the clipboard. */
  private copySelection(): void {
    if (this.selectedId == null || !this.gizmoTarget) return;
    const obj = this.gizmoTarget;
    const prefabKey = (obj.userData.prefabKey as string | undefined) ?? this.brushKey;
    const kind = (obj.userData.kind as string | undefined) ?? this.brushKind;
    if (!prefabKey) return;
    this.clipboard = {
      prefabKey,
      kind,
      quaternion: obj.quaternion.clone(),
      scale: obj.scale.clone(),
    };
    console.info(`[AtlasAdminEditor] copied ${prefabKey}`);
  }

  /**
   * Paste the clipboard entry at the terrain point under the fly-camera crosshair.
   * Reuses the Phase-2 terrain ray-march; requires pointer-locked fly mode.
   */
  private paste(): void {
    if (!this.clipboard) return;
    const conn = atlas.conn;
    if (!conn) return;
    if (!atlas.flyMode || document.pointerLockElement !== this.canvas) {
      console.info('[AtlasAdminEditor] paste: requires pointer-locked fly mode (crosshair)');
      return;
    }
    const body = atlas.localMesh;
    if (!body) return;
    const cam = body.children[0] as THREE.Camera | undefined;
    if (!cam) return;

    const commons = [...conn.db.world.iter()].find((w) => w.kind === 'commons');
    if (!commons) return;
    const seed = commons.terrainSeed;

    this.raycaster.setFromCamera(new THREE.Vector2(0, 0), cam);
    const ray = this.raycaster.ray;
    const p = ray.origin.clone();
    let placed = false;
    for (let i = 0; i < 400; i++) {
      p.addScaledVector(ray.direction, 1);
      // meshHeight, not terrainHeight (high-relief audit 2026-08-05): the
      // analytic field sits above the rendered mesh mid-cell on slopes, so
      // ray-marched placements landed visibly airborne on steep ground.
      const gy = meshHeight(seed, p.x, p.z);
      if (p.y <= gy) {
        const q = this.clipboard.quaternion;
        const s = this.clipboard.scale;
        this._pendingUndoCount++; // the id arrives via onInsert (mine only); a refusal releases the slot
        this.sent((conn.reducers as unknown as AdminReducers).adminPlaceObject({
          worldId: atlas.commonsWorldId ?? 0n, // the REAL Commons id (autoInc), NOT the 0n sentinel
          prefabKey: this.clipboard.prefabKey,
          kind: this.clipboard.kind,
          posX: p.x,
          posY: gy,
          posZ: p.z,
          rotX: q.x,
          rotY: q.y,
          rotZ: q.z,
          rotW: q.w,
          scaleX: s.x,
          scaleY: s.y,
          scaleZ: s.z,
        }), 'paste', this.unpend);
        placed = true;
        break;
      }
    }
    if (!placed) {
      console.info('[AtlasAdminEditor] paste: no terrain hit within 400 m');
    }
  }

  // ─── Phase 4: undo ───────────────────────────────────────────────────────────

  /**
   * Apply the top undo entry.
   *   `place`     → adminDeleteObject (undoes the place).
   *   `transform` → adminTransformObject with the before-state.
   *
   * Delete-undo (re-placing a deleted object) and redo are intentionally absent:
   * re-placing would assign a new server-generated placementId that breaks any
   * earlier undo entries referencing the original id.
   */
  private applyUndo(): void {
    const entry = this.undoStack.pop();
    if (!entry) return;
    const conn = atlas.conn;
    if (!conn) return;

    if (entry.kind === 'place') {
      // A refusal restores the entry so the user can retry (the rejection is async).
      this.sent((conn.reducers as unknown as AdminReducers).adminDeleteObject({
        placementId: entry.placementId,
      }), 'undo place', () => this.undoStack.push(entry));
      if (this.selectedId === entry.placementId) this.clearSelection();
    } else {
      const b = entry.before;
      try {
        this.sent((conn.reducers as unknown as AdminReducers).adminTransformObject({
          placementId: entry.placementId,
          posX: b.posX, posY: b.posY, posZ: b.posZ,
          rotX: b.rotX, rotY: b.rotY, rotZ: b.rotZ, rotW: b.rotW,
          scaleX: b.scaleX, scaleY: b.scaleY, scaleZ: b.scaleZ,
        }), 'undo transform', () => { this.revertTransform(entry.placementId); this.undoStack.push(entry); });
        // If this is the currently selected object, also apply locally so the
        // gizmo + panel reflect the undone state without waiting for the round-trip.
        if (this.selectedId === entry.placementId && this.gizmoTarget) {
          const obj = this.gizmoTarget;
          obj.position.set(b.posX, b.posY, b.posZ);
          obj.quaternion.set(b.rotX, b.rotY, b.rotZ, b.rotW);
          obj.scale.set(b.scaleX, b.scaleY, b.scaleZ);
          this.refreshPanel();
          // Reset before-snapshot so the next commit has the correct anchor
          this._beforeTransform = { ...b };
        }
      } catch (err) {
        console.warn('[AtlasAdminEditor] undo transform failed:', err);
        this.undoStack.push(entry);
      }
    }
  }

  // ─── Phase 4: outliner panel ─────────────────────────────────────────────────

  private toggleOutliner(): void {
    if (this.outlinerVisible) this.closeOutliner();
    else this.openOutliner();
  }

  private openOutliner(): void {
    this.engaged = true;
    this.outlinerVisible = true;
    if (!this.outlinerPanel) this.buildOutlinerPanel();
    if (this.outlinerPanel) {
      this.outlinerPanel.style.display = 'flex';
      this.refreshOutliner();
    }
  }

  private closeOutliner(): void {
    this.outlinerVisible = false;
    if (this.outlinerPanel) this.outlinerPanel.style.display = 'none';
  }

  private buildOutlinerPanel(): void {
    const panel = document.createElement('div');
    panel.style.cssText =
      'position:fixed;top:12px;right:280px;width:250px;max-height:70vh;' +
      'display:none;flex-direction:column;z-index:9998;' +
      'background:color-mix(in srgb, var(--ax-bg) 92%, transparent);color:var(--ax-text);font:12px/1.45 var(--ax-mono);' +
      'padding:8px 10px;border:1px solid var(--ax-line2);border-radius:var(--ax-radius);' +
      'box-shadow:0 4px 20px rgba(0,0,0,.7)';

    const header = document.createElement('div');
    header.style.cssText =
      'font-weight:700;color:var(--ax-hi);margin-bottom:6px;font-size:13px;flex-shrink:0';
    header.textContent = 'Outliner  ( O to close )';
    panel.appendChild(header);

    const list = document.createElement('div');
    list.style.cssText = 'overflow-y:auto;flex:1;min-height:0';
    panel.appendChild(list);

    document.body.appendChild(panel);
    this.outlinerPanel = panel;
    this.outlinerList = list;
  }

  /**
   * Rebuild the outliner rows from the current `placements` map.
   * Groups placements by prefabKey, sorted alphabetically, with a count header
   * per group. Each row is a click target that frames + selects the placement.
   */
  private refreshOutliner(): void {
    const list = this.outlinerList;
    if (!list) return;

    // Clear existing content
    while (list.firstChild) list.removeChild(list.firstChild);

    if (this.placements.size === 0) {
      const empty = document.createElement('div');
      empty.textContent = 'No placements';
      empty.style.cssText = 'color:var(--ax-muted);padding:4px;font-size:11px';
      list.appendChild(empty);
      return;
    }

    // Group placements by prefabKey
    const groups = new Map<string, Array<{ id: string; obj: THREE.Object3D }>>();
    for (const [id, obj] of this.placements) {
      const key = (obj.userData.prefabKey as string | undefined) ?? '(unknown)';
      let grp = groups.get(key);
      if (!grp) { grp = []; groups.set(key, grp); }
      grp.push({ id, obj });
    }

    const sortedKeys = [...groups.keys()].sort();
    for (const key of sortedKeys) {
      const items = groups.get(key)!;

      // Group header
      const hdr = document.createElement('div');
      hdr.textContent = `${key}  (${items.length})`;
      hdr.style.cssText =
        'color:var(--ax-hi);font-size:10px;margin:4px 0 2px;font-weight:600;' +
        'white-space:nowrap;overflow:hidden;text-overflow:ellipsis';
      list.appendChild(hdr);

      // Rows sorted by numeric id for stable ordering
      items.sort((a, b) => {
        const diff = Number(BigInt(a.id) - BigInt(b.id));
        return diff < 0 ? -1 : diff > 0 ? 1 : 0;
      });

      for (const { id, obj } of items) {
        const placementId = obj.userData.placementId as bigint | undefined;
        if (placementId == null) continue;

        const isSel = this.selectedId === placementId;
        const row = document.createElement('button');
        row.textContent = `${key} #${placementId}`;
        row.title = 'Click to select and frame';
        row.style.cssText =
          `width:100%;text-align:left;` +
          `background:${isSel ? 'var(--ax-sel)' : 'transparent'};` +
          `color:${isSel ? 'var(--ax-hi)' : 'var(--ax-text)'};border:none;border-radius:var(--ax-radius);` +
          `padding:2px 4px;cursor:pointer;font:11px var(--ax-mono);margin-bottom:1px;` +
          `overflow:hidden;white-space:nowrap;text-overflow:ellipsis;display:block`;
        row.addEventListener('click', () => { this.frameAndSelect(placementId, obj); });
        list.appendChild(row);
      }
    }
  }

  /**
   * Move the fly camera to frame an object (8 m back, 5 m above), then select it.
   * If not already in fly mode, enables it so the user can immediately interact.
   * Called from the outliner row click.
   */
  private frameAndSelect(id: bigint, obj: THREE.Object3D): void {
    if (!atlas.flyMode) atlas.flyMode = true;

    const body = atlas.localMesh;
    if (body) {
      // Position the camera 8 m back along world-Z and 5 m up from the object
      body.position.set(
        obj.position.x,
        obj.position.y + 5,
        obj.position.z + 8,
      );
      // Orient to look toward the object
      const dx = obj.position.x - body.position.x;
      const dz = obj.position.z - body.position.z;
      if (dx * dx + dz * dz > 0.0001) {
        atlas.localYaw = Math.atan2(-dx, -dz);
      }
    }

    this.setSelected(id, obj);

    // Refresh row highlight after setSelected updates selectedId
    if (this.outlinerVisible) this.refreshOutliner();
  }

  // ─── Lifecycle ───────────────────────────────────────────────────────────────

  dispose(): void {
    // Wave-3: instanced representation teardown (removeInstanced drops each collider too)
    for (const key of [...this.instHandles.keys()]) this.removeInstanced(key);
    for (const key of [...this.colliderOn.keys()]) this.dropCollider(key);
    this.instancer?.dispose();
    this.instancer = null;
    this.placeholderGeo?.dispose();
    this.placeholderGeo = null;
    this.placeholderMat?.dispose();
    this.placeholderMat = null;
    this.rowCache.clear();

    this.keys.clear();
    this.hint?.remove();
    this.hint = null;
    this.crosshair?.remove();
    this.crosshair = null;
    this.palette?.remove();
    this.palette = null;
    this.transformPanel?.remove();
    this.transformPanel = null;
    this.panelInputs = null;
    // Phase 4: outliner
    this.outlinerPanel?.remove();
    this.outlinerPanel = null;
    this.outlinerList = null;
    this.outlinerVisible = false;
    this.undoStack.length = 0;
    this._beforeTransform = null;
    this.clipboard = null;
    if (this.mouseHandler) {
      this.canvas?.removeEventListener('mousedown', this.mouseHandler);
      this.mouseHandler = null;
    }
    if (this.keyHandler2) {
      window.removeEventListener('keydown', this.keyHandler2);
      this.keyHandler2 = null;
    }
    // Phase 3: remove gizmo helper from scene
    if (this.gizmoHelper && this.scene) {
      this.scene.remove(this.gizmoHelper);
      this.gizmoHelper = null;
    }
    if (this.gizmo) {
      this.gizmo.detach();
      this.gizmo.dispose();
      this.gizmo = null;
    }
    this.gizmoTarget = null;
    atlas.editorCursorMode = false;

    this.clearSelection();
    // Dispose all placement instances
    for (const inst of this.placements.values()) {
      const assetId = inst.userData.assetId as bigint | undefined;
      if (assetId && assetId !== 0n) this.assets.dispose(assetId);
    }
    this.placements.clear();
    if (this.scene) this.scene.remove(this.placementGroup);
  }
}
